import { computed, shallowRef, useHost, type ComputedRef, type ToRefs, type Ref } from "vue";
import { useComponentContext } from "composables/useComponentContext";
import { useStateStore, getElementByUid } from "composables/useStateStore";
import { useSharedState } from "composables/useSharedState";
import { createEventDispatcher } from "@/runtime/shared/events";
import { closestAcrossShadowBoundaries } from "@/runtime/shared/shadow-dom";
import type { CheckboxProps, CheckboxGroupProps, CheckedState, CheckboxModelValue } from "./checkbox.types";
import type { AcceptableValue } from "@/runtime/types/utils";
import { checkboxRootClasses, checkboxBoxClasses, checkboxIndicatorClasses } from "./checkbox.styles";

const CHECKBOX_TYPE = "Checkbox";

/**
 * Registry mapping DOM elements to their Vue component UIDs and state.
 */
const checkboxRegistry = new WeakMap<Element, { uid: number; state: ToRefs<CheckboxProps> }>();

/**
 * Registers a Checkbox element with its component UID and reactive state.
 */
export const registerCheckbox = (element: Element, uid: number, state: ToRefs<CheckboxProps>): void => {
  checkboxRegistry.set(element, { uid, state });
};

export interface UseCheckboxOptions {
  props?: Readonly<CheckboxProps>;
  buttonRef?: Ref<HTMLButtonElement | null>;
}

export interface UseCheckboxReturn {
  // Checkbox context
  checkboxGroupUid: ComputedRef<number | undefined>;
  checkboxGroupProps: ComputedRef<CheckboxGroupProps | undefined>;
  checkboxGroupElement: ComputedRef<Element | null>;

  // State
  state: ToRefs<CheckboxProps>;

  // Computed properties
  rootClasses: ComputedRef<ReturnType<typeof checkboxRootClasses>>;
  boxClasses: ComputedRef<ReturnType<typeof checkboxBoxClasses>>;
  finalColor: ComputedRef<string>;
  finalVariant: ComputedRef<string>;
  finalSize: ComputedRef<string>;
  ariaChecked: ComputedRef<boolean | "mixed">;
  ariaDisabled: ComputedRef<boolean | undefined>;
  dataDisabled: ComputedRef<boolean | undefined>;
  dataState: ComputedRef<"checked" | "unchecked" | "indeterminate">;
  formValue: ComputedRef<AcceptableValue | undefined>;
  isChecked: ComputedRef<boolean>;
  isIndeterminate: ComputedRef<boolean>;

  // Indicator properties
  shouldShowIndicator: ComputedRef<boolean>;
  indicatorIcon: ComputedRef<string>;
  indicatorSize: ComputedRef<string>;
  indicatorClasses: ComputedRef<ReturnType<typeof checkboxIndicatorClasses>>;

  // Methods
  handleClick: () => void;

  // Component wrapper
  WrapperComponent: ComputedRef<"e-card" | "div">;
  onRootClick: () => void;
  onButtonClick: () => void;
  onSlotClick: () => void;
}

/** Normalize model value from string attributes to boolean. */
const normalizeModelValue = (raw: CheckboxModelValue): CheckboxModelValue => {
  if (raw === "true") return true;
  if (raw === "false") return false;
  if (raw === undefined) return false;
  return raw;
};

/** Check if a checkbox value exists in the group's model value array. */
const isCheckedInGroup = (
  stateStore: ReturnType<typeof useStateStore>,
  groupUid: number | undefined,
  checkboxValue: AcceptableValue | undefined,
): boolean => {
  if (groupUid == null) return false;

  const groupComponent = stateStore.getByUid(groupUid);
  const groupProps = groupComponent?.props as CheckboxGroupProps | undefined;
  const groupModelValue = (groupProps?.modelValue as AcceptableValue[]) ?? [];

  return Array.isArray(groupModelValue) && checkboxValue != null && groupModelValue.includes(checkboxValue);
};

/** Compute the checked state of a checkbox. */
const computeIsChecked = (
  currentState: ToRefs<CheckboxProps> | null,
  stateStore: ReturnType<typeof useStateStore>,
  groupUid: number | undefined,
): boolean => {
  if (!currentState) return false;

  const modelValue = normalizeModelValue(currentState.modelValue?.value as CheckboxModelValue);

  if (groupUid != null) {
    return isCheckedInGroup(stateStore, groupUid, currentState.value?.value);
  }

  return modelValue === true;
};

/** Resolve a property from local state, group props, or a default. */
const resolveInherited = (
  currentState: ToRefs<CheckboxProps> | null,
  groupProps: CheckboxGroupProps | undefined,
  prop: keyof CheckboxProps & keyof CheckboxGroupProps,
  fallback: string,
): string => {
  return (currentState as any)?.[prop]?.value ?? (groupProps as any)?.[prop] ?? fallback;
};

/** Determine the checked-state string for box classes. */
const toCheckedState = (isIndeterminate: boolean, isChecked: boolean): CheckedState => {
  if (isIndeterminate) return "indeterminate";
  return isChecked ? "true" : "false";
};

/** Handle click for a grouped checkbox. */
const handleGroupClick = (
  stateStore: ReturnType<typeof useStateStore>,
  groupUid: number,
  currentState: ToRefs<CheckboxProps> | null,
  isChecked: boolean,
  groupElement: Element | null,
): void => {
  if (!currentState) return;

  const groupComponent = stateStore.getByUid(groupUid);
  const groupProps = groupComponent?.props as CheckboxGroupProps | undefined;
  const groupModelValue = (groupProps?.modelValue as AcceptableValue[]) ?? [];
  const checkboxValue = currentState.value?.value;

  if (checkboxValue == null) return;

  const newGroupValue = isChecked
    ? groupModelValue.filter((v) => v !== checkboxValue)
    : [...groupModelValue, checkboxValue];

  if (groupElement) {
    createEventDispatcher(groupElement)("update:modelValue", newGroupValue);
  }
};

const sizeToIconSizeMap: Record<string, string> = {
  xs: "xs",
  sm: "xs",
  md: "sm",
  lg: "md",
  xl: "md",
};

/** Resolve checkbox context: parent state, group element, group props. */
const resolveCheckboxContext = (
  host: Element | null,
  context: ReturnType<typeof useComponentContext>,
  props: Readonly<CheckboxProps> | undefined,
  state: ToRefs<CheckboxProps>,
  stateStore: ReturnType<typeof useStateStore>,
) => {
  const cachedCheckboxState = shallowRef<ToRefs<CheckboxProps> | null>(null);

  if (host && context.typeName !== CHECKBOX_TYPE) {
    const parentElement = closestAcrossShadowBoundaries(host, "e-checkbox");
    if (parentElement) {
      const parentData = checkboxRegistry.get(parentElement);
      if (parentData) cachedCheckboxState.value = parentData.state;
    }
  }

  const checkboxState = computed(() =>
    context.typeName === CHECKBOX_TYPE && props ? state : cachedCheckboxState.value,
  );

  const checkboxGroupUid = computed(() => {
    if (context.uid == null) return;
    return stateStore.findParentOfType(context.uid, "CheckboxGroup")?.uid ?? undefined;
  });
  const checkboxGroupElement = computed(() => {
    const uid = checkboxGroupUid.value;
    if (uid == null) return null;
    return (getElementByUid(uid) as Element) ?? null;
  });
  const checkboxGroupProps = computed(() => {
    const uid = checkboxGroupUid.value;
    return uid == null ? undefined : (stateStore.getByUid(uid)?.props as CheckboxGroupProps | undefined);
  });

  return { checkboxState, checkboxGroupElement, checkboxGroupUid, checkboxGroupProps };
};

/** Build aria, data, and indicator computed properties for a checkbox. */
const buildCheckboxIndicatorAndAria = (
  isChecked: ComputedRef<boolean>,
  isIndeterminate: ComputedRef<boolean>,
  isDisabled: ComputedRef<boolean>,
  checkboxState: ComputedRef<ToRefs<CheckboxProps> | null>,
  finalSize: ComputedRef<string>,
  state: ToRefs<CheckboxProps>,
) => {
  const ariaChecked = computed<boolean | "mixed">(() => (isIndeterminate.value ? "mixed" : isChecked.value));
  const ariaDisabled = computed(() => (isDisabled.value ? true : undefined));
  const dataDisabled = computed(() => (isDisabled.value ? true : undefined));
  const dataState = computed<"checked" | "unchecked" | "indeterminate">(() => {
    if (isIndeterminate.value) return "indeterminate";
    return isChecked.value ? "checked" : "unchecked";
  });
  const formValue = computed(() => (isChecked.value ? (checkboxState.value?.value?.value ?? "on") : undefined));
  const shouldShowIndicator = computed(() => isChecked.value || isIndeterminate.value);
  const indicatorIcon = computed(() => (isIndeterminate.value ? "minus-small" : (state.icon?.value ?? "check")));
  const indicatorSize = computed(() => sizeToIconSizeMap[finalSize.value] ?? "sm");
  const indicatorClasses = computed(() =>
    checkboxIndicatorClasses({ visible: shouldShowIndicator.value ? "true" : "false" }),
  );
  return {
    ariaChecked,
    ariaDisabled,
    dataDisabled,
    dataState,
    formValue,
    shouldShowIndicator,
    indicatorIcon,
    indicatorSize,
    indicatorClasses,
  };
};

/** Build all derived computed properties for a checkbox. */
const buildCheckboxComputedProps = (
  checkboxState: ComputedRef<ToRefs<CheckboxProps> | null>,
  checkboxGroupProps: ComputedRef<CheckboxGroupProps | undefined>,
  checkboxGroupUid: ComputedRef<number | undefined>,
  stateStore: ReturnType<typeof useStateStore>,
  state: ToRefs<CheckboxProps>,
) => {
  const isDisabled = computed(() => {
    const s = checkboxState.value;
    return s ? (s.disabled?.value ?? false) || (checkboxGroupProps.value?.disabled ?? false) : false;
  });
  const isChecked = computed(() => computeIsChecked(checkboxState.value, stateStore, checkboxGroupUid.value));
  const isIndeterminate = computed(() => checkboxState.value?.modelValue?.value === "indeterminate");
  const finalColor = computed(() =>
    resolveInherited(checkboxState.value, checkboxGroupProps.value, "color", "neutral"),
  );
  const finalVariant = computed(() =>
    resolveInherited(checkboxState.value, checkboxGroupProps.value, "variant", "subtle"),
  );
  const finalSize = computed(() => resolveInherited(checkboxState.value, checkboxGroupProps.value, "size", "md"));
  const rootClasses = computed(() =>
    checkboxRootClasses({ disabled: isDisabled.value, encased: !!checkboxState.value?.encased?.value }),
  );
  const boxClasses = computed(() =>
    checkboxBoxClasses({
      color: finalColor.value as NonNullable<Parameters<typeof checkboxBoxClasses>[0]>["color"],
      variant: finalVariant.value as NonNullable<Parameters<typeof checkboxBoxClasses>[0]>["variant"],
      size: finalSize.value as NonNullable<Parameters<typeof checkboxBoxClasses>[0]>["size"],
      disabled: isDisabled.value,
      checked: toCheckedState(isIndeterminate.value, isChecked.value),
    }),
  );
  const ia = buildCheckboxIndicatorAndAria(isChecked, isIndeterminate, isDisabled, checkboxState, finalSize, state);
  return {
    isDisabled,
    isChecked,
    isIndeterminate,
    finalColor,
    finalVariant,
    finalSize,
    rootClasses,
    boxClasses,
    ...ia,
  };
};

/**
 * Build the encased/non-encased click handler trio.
 *
 * - `onRootClick` fires only in encased mode (the e-card wraps the whole row).
 * - `onButtonClick` / `onSlotClick` fire only in plain mode (no wrapper).
 */
const buildClickHandlers = (encased: boolean | undefined, handleClick: () => void) => {
  const onRootClick = () => {
    if (encased) handleClick();
  };
  const onButtonClick = () => {
    if (!encased) handleClick();
  };
  const onSlotClick = () => {
    if (!encased) handleClick();
  };
  return { onRootClick, onButtonClick, onSlotClick };
};

export const useCheckbox = (options: UseCheckboxOptions = {}): UseCheckboxReturn => {
  const { props } = options;
  const stateStore = useStateStore();
  const context = useComponentContext();
  const host = useHost();
  const state = props ? useSharedState(props) : ({} as ToRefs<CheckboxProps>);
  const dispatchEvent = createEventDispatcher(host);

  if (props && host && context.typeName === CHECKBOX_TYPE && context.uid != null) {
    registerCheckbox(host, context.uid, state);
  }

  const ctx = resolveCheckboxContext(host, context, props, state, stateStore);
  const cp = buildCheckboxComputedProps(
    ctx.checkboxState,
    ctx.checkboxGroupProps,
    ctx.checkboxGroupUid,
    stateStore,
    state,
  );

  const handleClick = () => {
    if (cp.isDisabled.value || !host) return;
    const groupUid = ctx.checkboxGroupUid.value;
    if (groupUid == null) {
      const newValue = !cp.isChecked.value;
      dispatchEvent("update:modelValue", newValue);
      dispatchEvent("change", newValue);
      return;
    }
    handleGroupClick(stateStore, groupUid, ctx.checkboxState.value, cp.isChecked.value, ctx.checkboxGroupElement.value);
  };

  if (host) (host as HTMLElement).focus = () => options.buttonRef?.value?.focus();

  const WrapperComponent = computed(() => (props?.encased ? "e-card" : "div"));
  const clickHandlers = buildClickHandlers(props?.encased, handleClick);

  return {
    checkboxGroupUid: ctx.checkboxGroupUid,
    checkboxGroupProps: ctx.checkboxGroupProps,
    checkboxGroupElement: ctx.checkboxGroupElement,
    state: ctx.checkboxState.value ?? ({} as ToRefs<CheckboxProps>),
    ...cp,
    handleClick,
    WrapperComponent,
    ...clickHandlers,
  };
};
